import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

import { formatEmail, isValidEmailFormat } from '@shared';

export interface EmailTag {
  value: string;
  isValid: boolean;
  error: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
}

interface UseEmailTagInputOptions {
  emails: EmailTag[];
  onChange: (emails: EmailTag[]) => void;
  maxEmails: number;
}

function validateEmail(email: string): { isValid: boolean; error: string } {
  if (!isValidEmailFormat(email)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  return { isValid: true, error: '' };
}

function parseValues(text: string): string[] {
  return text
    .split(/[;,\n]+/)
    .map(formatEmail)
    .map(s => s.trim())
    .filter(Boolean);
}

export function useEmailTagInput({ emails, onChange, maxEmails }: UseEmailTagInputOptions) {
  const [inputValue, setInputValue] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const hasReachedLimit = emails.length >= maxEmails;

  useEffect(() => {
    if (!inputValue) setDuplicateWarning('');
  }, [inputValue]);

  function isDuplicate(value: string, existing = emails) {
    return existing.some(r => r.value.toLowerCase() === value.toLowerCase());
  }

  function addEmailsFromText(text: string) {
    if (hasReachedLimit) return;

    const values = parseValues(text);
    const newEmails: EmailTag[] = [];
    const duplicates: string[] = [];
    const seen = new Set<string>();

    for (const val of values) {
      const lower = val.toLowerCase();

      if (isDuplicate(val) || seen.has(lower)) {
        duplicates.push(val);
        continue;
      }

      seen.add(lower);
      const validation = validateEmail(val);
      newEmails.push({ value: val, isValid: validation.isValid, error: validation.error });
    }

    if (duplicates.length) {
      setDuplicateWarning(`${[...new Set(duplicates)].join(', ')} already added`);
    } else {
      setDuplicateWarning('');
    }

    const available = maxEmails - emails.length;
    const limited = newEmails.slice(0, available);

    onChange([...emails, ...limited]);
  }

  function removeEmail(value: string) {
    onChange(emails.filter(r => r.value !== value));
    setEditingIndex(null);
    setEditingValue('');
    setDuplicateWarning('');
  }

  function commitEdit() {
    if (editingIndex === null) return;
    const val = formatEmail(editingValue).trim();

    const next = [...emails];

    if (!val) {
      next.splice(editingIndex, 1);
    } else {
      const dup = next.some(
        (r, i) => i !== editingIndex && r.value.toLowerCase() === val.toLowerCase(),
      );
      if (dup) {
        setDuplicateWarning(`${val} already added`);
        setEditingIndex(null);
        setEditingValue('');
        return;
      }
      const validation = validateEmail(val);
      next[editingIndex] = {
        ...next[editingIndex],
        value: val,
        isValid: validation.isValid,
        error: validation.error,
      };
    }

    onChange(next);
    setEditingIndex(null);
    setEditingValue('');
  }

  function startEditing(index: number, value: string) {
    setEditingIndex(index);
    setEditingValue(value);
  }

  function cancelEditing() {
    setEditingIndex(null);
    setEditingValue('');
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const key = e.key;

    if (['Enter', 'Tab', ',', ';', ' '].includes(key) && inputValue.trim()) {
      e.preventDefault();
      addEmailsFromText(inputValue);
      setInputValue('');
      return;
    }

    if (key === 'Backspace' && inputValue === '' && emails.length > 0) {
      removeEmail(emails[emails.length - 1].value);
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    addEmailsFromText(pasted);
  }

  function handleInputBlur() {
    if (inputValue.trim()) {
      addEmailsFromText(inputValue);
      setInputValue('');
    }
  }

  return {
    inputValue,
    setInputValue,
    editingValue,
    setEditingValue,
    editingIndex,
    duplicateWarning,
    hasReachedLimit,
    inputRef,
    removeEmail,
    commitEdit,
    startEditing,
    cancelEditing,
    handleInputKeyDown,
    handlePaste,
    handleInputBlur,
  };
}
